// -----------------------------------------------------------------------------
// src/pages/.../components/creatorSettings/CreatorSettingsPanel.jsx
// FULL, self-contained component – copy-paste ready
// -----------------------------------------------------------------------------
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
  Typography,
  Checkbox,
  TextField,
  Tooltip,
} from '@mui/material';
import client from '../../../../API';

export default function CreatorSettingsPanel({ creator, onUpdated }) {
  /* ───────── helpers ───────── */
  const creatorId = creator.id ?? creator.creator_id;         // <— fix for missing ID
  if (!creatorId) {
    return (
      <Alert severity="error">
        Creator record does not include an <code>id</code> or <code>creator_id</code>.
      </Alert>
    );
  }

  /* ───────── initial values (for dirty-check) ───────── */
  const initial = useRef({
    emailCreated: !!creator.email_created,
    emailInBio:   !!creator.email_in_bio,
    processEmails: creator.process_emails !== undefined ? creator.process_emails : true,
    forwardedAlias: creator.forwarded_alias || '',
  });

  /* ───────── state ───────── */
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const [emailCreated,   setEmailCreated]   = useState(initial.current.emailCreated);
  const [emailInBio,     setEmailInBio]     = useState(initial.current.emailInBio);
  const [processEmails,  setProcessEmails]  = useState(initial.current.processEmails);
  const [forwardedAlias, setForwardedAlias] = useState(initial.current.forwardedAlias);
  const [status,         setStatus]         = useState(creator.status || 'Associate');

  /* ───────── dirty-tracking ───────── */
  const isDirty =
    emailCreated   !== initial.current.emailCreated   ||
    emailInBio     !== initial.current.emailInBio     ||
    processEmails  !== initial.current.processEmails  ||
    forwardedAlias !== initial.current.forwardedAlias;

  /* ───────── update helper ───────── */
  const updateCreator = async payload => {
    setSaving(true);
    setError(null);
    try {
      await client.creators.update({ creator_id: creatorId, ...payload });
      onUpdated?.(payload);

      // reset dirty state reference after successful save
      initial.current = {
        emailCreated,
        emailInBio,
        processEmails,
        forwardedAlias,
      };
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Failed to update creator';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ───────── toolbar actions ───────── */
  const handleSave = async () => {
    const payload = {
      email_created:   emailCreated,
      email_in_bio:    emailInBio,
      process_emails:  processEmails,
      forwarded_alias: processEmails ? forwardedAlias : null,
    };
    await updateCreator(payload);
  };

  /* ───────── status logic (immediate API call) ───────── */
  const requirementsMet = emailCreated && processEmails;

  const moveToPartner = async () => {
    await updateCreator({ status: 'Partner' });
    setStatus('Partner');
  };

  const moveToAssociate = async () => {
    await updateCreator({ status: 'Associate', process_emails: false });
    setStatus('Associate');
    setProcessEmails(false);
    setForwardedAlias('');
  };

  /* ───────── render ───────── */
  return (
    <Box sx={{ px: 2, py: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── basic toggles ── */}
      <FormControlLabel
        control={
          <Switch
            checked={emailCreated}
            onChange={e => setEmailCreated(e.target.checked)}
          />
        }
        label="Email Alias Created"
      />
      <FormControlLabel
        control={
          <Switch
            checked={emailInBio}
            onChange={e => setEmailInBio(e.target.checked)}
          />
        }
        label="Email in Bio"
      />

      {/* ── forwarding ── */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Email Forwarding
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={processEmails}
            onChange={e => {
              const next = e.target.checked;
              setProcessEmails(next);
              if (!next) setForwardedAlias('');
            }}
          />
        }
        label="Enable Email Forwarding"
      />
      <TextField
        variant="outlined"
        margin="dense"
        fullWidth
        disabled={!processEmails}
        label="Forwarding Alias Email"
        value={forwardedAlias}
        onChange={e => setForwardedAlias(e.target.value)}
      />

      {/* ── save button ── */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!isDirty || saving}
          onClick={handleSave}
        >
          Save Changes
        </Button>
        {isDirty && !saving && (
          <Typography variant="caption" sx={{ ml: 1 }}>
            Unsaved changes
          </Typography>
        )}
      </Box>

      {/* ── status controls ── */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Partner Status
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Current Status:&nbsp;<strong>{status}</strong>
      </Typography>

      {status === 'Partner' ? (
        <Button
          variant="contained"
          color="warning"
          disabled={saving}
          onClick={moveToAssociate}
          sx={{ mb: 2 }}
        >
          Move to Associate
        </Button>
      ) : (
        <Tooltip
          title={
            requirementsMet
              ? ''
              : 'Requires: Email Alias Created AND Email Forwarding enabled'
          }
        >
          <span>
            <Button
              variant="contained"
              color="success"
              disabled={!requirementsMet || saving}
              onClick={moveToPartner}
              sx={{ mb: 2 }}
            >
              Move to Partner
            </Button>
          </span>
        </Tooltip>
      )}

      {/* ── danger zone ── */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Danger Zone
      </Typography>
      <Button
        color="error"
        variant="contained"
        disabled={saving}
        onClick={async () => {
          if (
            !window.confirm(
              'Remove yourself as supervisor for this creator? You can re-assign later.'
            )
          )
            return;
          await updateCreator({ superviser: null });
        }}
      >
        Stop Supervising
      </Button>

      {saving && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
    </Box>
  );
}
